import React, { useState, useCallback, useMemo, useEffect } from "react";
import { connect } from "react-redux";
import { bindActionCreators, Dispatch } from "redux";
import { ICommandBarItemProps, IContextualMenuItem, PersonaSize } from "office-ui-fabric-react";
import { ApplicationState } from "../../../../store";
import { ApprovalRequest, ApprovalRequestStatus } from "../../../../store/ApprovalCenterStore";
import * as TimesheetApprovalStore from "../../../../store/TimesheetApprovalStore";
import { TimesheetApprovalDetails } from "../../../../store/TimesheetApprovalStore";
import {
    DelegationScope,
    actionCreators as delegationActionCreators,
    getActingAs,
    getCandidates,
    isDelegationEnabled
} from "../../../../store/DelegationStore";
import { PersonInfo } from "../../../common/inputs/PersonPickerInput";
import { DelegationModeBanner, StartDelegationModeButton } from "../../DelegationMode";
import { FilterKeys } from "../../../../store/filters";
import * as TimeReportingPeriodsStore from "../../../../store/TimeReportingPeriodsStore";
import { TimeReportingPeriod, findPeriodForDate, isPeriodOpenForReporting } from "../../../../store/TimeReportingPeriodsStore";
import * as Metadata from "../../../../entities/Metadata";
import { nameof } from "../../../../store/services/metadataService";
import { ApprovalRequestAttrs } from "../../../../store/ApprovalCenterStore";
import ApprovalTabContent from "../../ApprovalTabContent";
import {
    ApprovalTabActions,
    ApprovalTabSpec,
    ApprovalTabState,
    buildApprovalTabActions,
    buildSaveApprovalTabSetting,
    DecisionPopupProps,
    selectApprovalTabState
} from "../../approvalTabBase";
import GenericCommentPopup from "../../GenericCommentPopup";
import { ControlSettings, Views } from "../../../../store/services/viewSaver";
import TimeTrackingEntriesDialog from "./TimeTrackingEntriesDialog";
import BulkEditApprovalRequests from "./TimesheetApprovalEdit/BulkEditApprovalRequests";
import EditApprovalRequest from "./TimesheetApprovalEdit/EditApprovalRequest";
import { convertToDate, DateFilterValue } from "../../../common/filters/DateFilter";
import {
    timesheetFakeFields,
    timesheetFilterableFakeFields,
    buildPeriodFilterField,
    PeriodFilterField,
    Title,
    AdministrativeTimeLabel,
    PeriodField,
    TotalHoursField,
    TimesheetOwnerField,
    DecisionDateField,
    DecisionMakersField,
    ApproverCommentField
} from "./timesheetApprovalFields";
import EntityName from "../../../views/list/columns/EntityName";
import { commonFakeFieldValueGetters, commonFakeFieldRenderers } from "../../commonFakeFields";
import { FormatDate } from "../../../utils/common";
import ImagePersona from "../../../common/ImagePersona";
import ResourceFormatter from "../../../common/formatters/ResourceFormatter";
import { GridCellShowMoreComponent } from "../../../common/ShowMoreComponent/ShowMoreComponent";

type TimesheetApprovalRequest = ApprovalRequest<TimesheetApprovalDetails>;

const defaultDisplayFieldNames = [
    Title.name,
    nameof<ApprovalRequestAttrs>("Status"),
    PeriodField.name,
    TotalHoursField.name,
    TimesheetOwnerField.name,
    nameof<ApprovalRequestAttrs>("Submitter"),
    nameof<ApprovalRequestAttrs>("SubmittedDate"),
    nameof<ApprovalRequestAttrs>("SubmissionComment"),
    DecisionDateField.name
];

const allAvailableFieldNames = [
    ...defaultDisplayFieldNames,
    nameof<ApprovalRequestAttrs>("Approvers"),
    DecisionMakersField.name,
    ApproverCommentField.name
];

const mandatoryFieldNames = [Title.name, nameof<ApprovalRequestAttrs>("Status")];

type StateProps = ApprovalTabState<TimesheetApprovalDetails> & {
    timeReportingPeriods: TimeReportingPeriod[];
    canRecallApproved: boolean;
    isDelegationModeEnabled: boolean;
    delegationCandidates: PersonInfo[];
    actingAs?: PersonInfo;
};

type ActionProps = ApprovalTabActions<typeof TimesheetApprovalStore.actionCreators> & {
    timeReportingPeriodsActions: typeof TimeReportingPeriodsStore.actionCreators;
    delegationActions: typeof delegationActionCreators;
};

type Props = StateProps & ActionProps;

const timesheetDecisionPopupContent = {
    approve: {
        title: "Approve Request",
        subText:
            "Are you sure you want to approve the reported hours? Provide the comment below for the submitter if necessary."
    },
    reject: {
        title: "Reject Request",
        subText: "Are you sure you want to reject the reported hours? Provide the comment below for the submitter."
    }
};

const TimesheetDecisionPopup = (props: DecisionPopupProps<TimesheetApprovalDetails>) => (
    <GenericCommentPopup {...props} content={timesheetDecisionPopupContent} />
);

const TimesheetApprovalTab = (props: Props) => {
    const {
        requests,
        isLoading,
        isReadonlyMode,
        isSettingsLoading,
        settings,
        actions,
        serverFields,
        filters,
        activeFilter,
        canManageConfiguration,
        filtersActions,
        timeReportingPeriodsActions,
        timeReportingPeriods,
        preFilterId,
        canRecallApproved,
        currentUserId,
        isAdmin,
        isDelegationModeEnabled,
        delegationCandidates,
        actingAs,
        delegationActions
    } = props;
    const [selectedRequestForTimeEntries, setSelectedRequestForTimeEntries] = useState<TimesheetApprovalRequest | null>(
        null
    );
    const [bulkEditRequestIds, setBulkEditRequestIds] = useState<string[] | null>(null);
    const [editRequestId, setEditRequestId] = useState<string | null>(null);

    const canRecall = useCallback(
        (item: ApprovalRequest<TimesheetApprovalDetails>) => {
            if (item.attributes.Status !== ApprovalRequestStatus.Approved || actingAs) {
                return false;
            }
            if (isAdmin) {
                return true;
            }
            if (!canRecallApproved) {
                return false;
            }
            return item.attributes.Approvers.some(a => a.id === currentUserId);
        },
        [isAdmin, canRecallApproved, currentUserId, actingAs]
    );

    useEffect(() => {
        timeReportingPeriodsActions.loadPeriods();
        if (isDelegationModeEnabled) {
            delegationActions.loadSession(DelegationScope.TimesheetApproval);
        }
    }, []);

    const commandBarItems = useMemo((): ICommandBarItemProps[] | undefined =>
        !actingAs && delegationCandidates.length
            ? [{
                key: "startDelegationMode",
                onRender: () => (
                    <StartDelegationModeButton
                        candidates={delegationCandidates}
                        onStart={_ => delegationActions.startDelegationMode(DelegationScope.TimesheetApproval, _)} />
                )
            }]
            : undefined,
        [actingAs, delegationCandidates, delegationActions]);

    const preFilterOptions = useMemo((): Metadata.PreFilterOption<TimesheetApprovalRequest>[] => {
        const today = new Date();
        const currentPeriod = findPeriodForDate(today, timeReportingPeriods);

        const sortedPeriods = [...timeReportingPeriods].sort(
            (a, b) => new Date(b.attributes.StartDate).getTime() - new Date(a.attributes.StartDate).getTime()
        );

        const previousPeriod = currentPeriod
            ? sortedPeriods.find(p => new Date(p.attributes.EndDate) < new Date(currentPeriod.attributes.StartDate))
            : undefined;

        const options: Metadata.PreFilterOption<TimesheetApprovalRequest>[] = [];

        if (currentPeriod) {
            options.push({
                key: Metadata.ApprovalPrefilterKey.currentPeriod,
                name: "Current Period",
                predicate: request => request.details?.period?.id === currentPeriod.id
            });
        }

        if (previousPeriod) {
            options.push({
                key: Metadata.ApprovalPrefilterKey.previousPeriod,
                name: "Previous Period",
                predicate: request => request.details?.period?.id === previousPeriod.id
            });
        }

        return options;
    }, [timeReportingPeriods]);

    const controlSettings: ControlSettings = useMemo(
        () => ({
            ...settings,
            viewType: Views.List
        }),
        [settings]
    );

    const displayFieldNames = useMemo(() => {
        const saved: string[] | undefined = settings?.displayFields;
        return saved?.length ? saved : defaultDisplayFieldNames;
    }, [settings?.displayFields]);

    const configurableFields = useMemo(
        () => [...serverFields, ...timesheetFakeFields].filter(f => allAvailableFieldNames.includes(f.name)),
        [serverFields]
    );

    const filterableFakeFields = useMemo(
        () => [...timesheetFilterableFakeFields, buildPeriodFilterField(timeReportingPeriods)],
        [timeReportingPeriods]
    );

    const saveSetting = useMemo(() => buildSaveApprovalTabSetting(actions.saveSettings), [actions]);

    const handleDisplayFieldsChange = useCallback((fields: string[]) => saveSetting.displayFields(fields), [saveSetting]);

    const fieldValueExtractor = useCallback((item: TimesheetApprovalRequest, field: Metadata.Field) => {
        if (!field.isFake) {
            return item.attributes[field.name];
        }

        const details = item.details;
        switch (field.name) {
            case Title.name:
                return details?.isAdministrative ? AdministrativeTimeLabel : (details?.project?.name ?? "");
            case PeriodField.name:
                return details?.period?.startDate ?? null;
            case PeriodFilterField.name:
                return details?.period?.id ?? null;
            case TotalHoursField.name:
                return details?.totalHours ?? null;
            case TimesheetOwnerField.name:
                return details?.timesheetOwner ?? null;
            case DecisionDateField.name:
                return commonFakeFieldValueGetters[DecisionDateField.name]!(item, currentUserId);
            case DecisionMakersField.name: {
                return item.decisions.map(d => d.approver);
            }
            case ApproverCommentField.name:
                return commonFakeFieldValueGetters[ApproverCommentField.name]!(item, currentUserId);
            default:
                return undefined;
        }
    }, []);

    const onItemRender = useCallback(
        (entity: TimesheetApprovalRequest, index: number, field: Metadata.Field, defaultRender: () => JSX.Element) => {
            if (field.name === Title.name) {
                return (
                    <EntityName
                        name={
                            entity.details?.isAdministrative
                                ? AdministrativeTimeLabel
                                : (entity.details?.project?.name ?? "")
                        }
                        iconName={entity.details?.isAdministrative ? "PPMXAdministrativeTime" : undefined}
                        imageClassName={entity.details?.isAdministrative ? "administrative-time-logo" : "proj-logo"}
                        onClick={() => setSelectedRequestForTimeEntries(entity)}
                    />
                );
            }

            if (field.name === PeriodField.name) {
                const period = entity.details?.period;
                const text = period ? `${FormatDate(period.startDate)} - ${FormatDate(period.endDate)}` : "-";
                return (
                    <span
                        className="clickable hover-underline"
                        onClick={() => setSelectedRequestForTimeEntries(entity)}
                        title={text}>
                        {text}
                    </span>
                );
            }

            if (field.name === TotalHoursField.name) {
                return <span>{entity.details?.totalHours != null ? `${entity.details.totalHours}h` : ""}</span>;
            }

            if (field.name === TimesheetOwnerField.name) {
                return (
                    <ImagePersona
                        size={PersonaSize.size24}
                        info={entity.details?.timesheetOwner}
                        text={entity.details?.timesheetOwner?.fullName ?? ""}
                    />
                );
            }

            if (field.name === nameof<ApprovalRequestAttrs>("SubmissionComment")) {
                const comment = entity.attributes.SubmissionComment;
                if (!comment) {
                    return <span />;
                }
                return (
                    <GridCellShowMoreComponent>
                        <span className="multiline" title={comment}>{comment}</span>
                    </GridCellShowMoreComponent>
                );
            }

            if (field.name === DecisionMakersField.name) {
                const decisionMakers = entity.decisions.map(d => d.approver);
                if (!decisionMakers.length) {
                    return <span />;
                }
                return <ResourceFormatter resource={decisionMakers} withNavigation={false} coinSize={PersonaSize.size24} />;
            }

            const commonRenderer = commonFakeFieldRenderers[field.name];
            if (commonRenderer) {
                return commonRenderer(entity, currentUserId);
            }

            return defaultRender();
        },
        []
    );

    const getSelectionCommands = useCallback(
        (selectedIds: string[]): ICommandBarItemProps[] =>
            actingAs
                ? []
                : [
                    {
                        key: "bulkEdit",
                        text: "Bulk Edit",
                        iconProps: { iconName: "TripleColumnEdit" },
                        disabled: isReadonlyMode,
                        onClick: () => setBulkEditRequestIds(selectedIds)
                    }
                ],
        [isReadonlyMode, actingAs]
    );

    const getItemCommands = useCallback(
        (item: TimesheetApprovalRequest): IContextualMenuItem[] => {
            if (actingAs) {
                return [];
            }
            const period = timeReportingPeriods.find(_ => _.id === item.details?.period?.id);
            const isEditable = item.attributes.Status === ApprovalRequestStatus.Pending || canRecall(item);
            return isEditable && isPeriodOpenForReporting(period)
                ? [{ key: "edit", name: "Edit", iconProps: { iconName: "Edit" }, onClick: () => setEditRequestId(item.id) }]
                : [];
        },
        [timeReportingPeriods, canRecall, actingAs]
    );

    return (
        <ApprovalTabContent
            key={actingAs?.id}
            requests={requests}
            isLoading={isLoading}
            isReadonlyMode={isReadonlyMode}
            actions={actions}
            serverFields={serverFields}
            fakeFields={timesheetFakeFields}
            filterableFakeFields={filterableFakeFields}
            displayFields={displayFieldNames}
            configurableFields={configurableFields}
            mandatoryFields={mandatoryFieldNames}
            onDisplayFieldsChange={handleDisplayFieldsChange}
            filters={filters}
            activeFilter={activeFilter}
            saveSetting={saveSetting}
            canManageConfiguration={canManageConfiguration}
            filtersActions={filtersActions}
            commandBarItems={commandBarItems}
            banner={<DelegationModeBanner scope={DelegationScope.TimesheetApproval} />}
            fieldValueExtractor={fieldValueExtractor}
            fieldCompareOverride={_timesheetFieldCompareOverride}
            onItemRender={onItemRender}
            controlSettings={controlSettings}
            onSaveSettings={isSettingsLoading ? undefined : actions.saveSettings}
            preFilterOptions={preFilterOptions}
            preFilterId={preFilterId}
            canRecall={canRecall}
            decisionPopup={TimesheetDecisionPopup}
            recallDialogContent={selectedRequests => {
                const isSingle = selectedRequests.length === 1;
                const allProjects = selectedRequests.every(r => !r.details?.isAdministrative);
                const allAdministrative = selectedRequests.every(r => r.details?.isAdministrative);
                const entityLabel = allProjects
                    ? "project"
                    : allAdministrative
                        ? "administrative time"
                        : "project/administrative time";

                return {
                    title: "Recall Approval",
                    subText: `Are you sure you want to recall approval for ${isSingle ? "this request" : "these requests"}? The approval ${isSingle ? "request" : "requests"} will be removed, and the ${entityLabel} will return to In Progress status.`,
                    note: `Note: The timesheet containing the recalled ${entityLabel} will also return to In Progress status. Project/administrative time that is not recalled will retain its current status.`
                };
            }}
            getSelectionCommands={getSelectionCommands}
            getItemCommands={getItemCommands}>
            {selectedRequestForTimeEntries && (
                <TimeTrackingEntriesDialog
                    request={selectedRequestForTimeEntries}
                    onDismiss={() => setSelectedRequestForTimeEntries(null)}
                />
            )}
            {bulkEditRequestIds && (
                <BulkEditApprovalRequests
                    requestIds={bulkEditRequestIds}
                    bulkEditPrepare={actions.bulkEditPrepare}
                    bulkEditValidate={actions.bulkEditValidate}
                    bulkEditApply={actions.bulkEditApply}
                    onClose={() => setBulkEditRequestIds(null)}
                />
            )}
            {editRequestId && (
                <EditApprovalRequest
                    requestId={editRequestId}
                    editPrepare={actions.editPrepare}
                    editValidate={actions.editValidate}
                    editApply={actions.editApply}
                    onClose={() => setEditRequestId(null)}
                />
            )}
        </ApprovalTabContent>
    );
};

const _timesheetFieldCompareOverride = (
    item: TimesheetApprovalRequest,
    field: Metadata.Field,
    fieldFilterValue: DateFilterValue
): boolean | undefined => {
    if (field.name !== PeriodField.name) {
        return undefined;
    }
    const period = item.details?.period;
    if (!period) {
        return false;
    }
    const filterFrom = convertToDate(fieldFilterValue.from);
    const filterTo = convertToDate(fieldFilterValue.to);

    return (
        (!filterFrom || period.endDate >= filterFrom.getBeginOfDay()) &&
        (!filterTo || period.startDate <= filterTo.getEndOfDay())
    );
};

const mapStateToProps = (state: ApplicationState): StateProps => ({
    ...selectApprovalTabState(state, state.timesheetApprovals, FilterKeys.TimesheetApproval),
    timeReportingPeriods: state.timeReportingPeriods.periods,
    canRecallApproved: state.tenant.timeTracking.globalSettings.allowManagersToRecallApprovedRequests,
    isDelegationModeEnabled: isDelegationEnabled(state.tenant, DelegationScope.TimesheetApproval),
    delegationCandidates: getCandidates(state, DelegationScope.TimesheetApproval),
    actingAs: getActingAs(state, DelegationScope.TimesheetApproval)
});

const mapDispatchToProps = (dispatch: Dispatch): ActionProps => ({
    ...buildApprovalTabActions(TimesheetApprovalStore.actionCreators, FilterKeys.TimesheetApproval)(dispatch),
    timeReportingPeriodsActions: bindActionCreators(TimeReportingPeriodsStore.actionCreators, dispatch),
    delegationActions: bindActionCreators(delegationActionCreators as any, dispatch) as typeof delegationActionCreators
});

const ConnectedTimesheetApprovalTab = connect(mapStateToProps, mapDispatchToProps)(TimesheetApprovalTab);

export default ConnectedTimesheetApprovalTab;

export const timesheetTabSpec: ApprovalTabSpec = {
    key: "timesheet",
    label: "Timesheet",
    isVisible: state => !!state.user.canApprove?.timesheet,
    selectPendingCount: state => state.timesheetApprovals.pendingCount,
    settings: {
        markLoading: TimesheetApprovalStore.actionCreators.markSettingsLoading,
        applyLoaded: TimesheetApprovalStore.actionCreators.applyLoadedSettings
    },
    loadPendingCount: TimesheetApprovalStore.actionCreators.loadPendingCount,
    render: () => <ConnectedTimesheetApprovalTab />
};
